import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Play, MoreHorizontal, Edit, Trash2, TestTube } from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp, getStatusVariant } from '../lib/utils'
import type { Test } from '../types/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

export function Tests() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['tests', page, statusFilter],
    queryFn: () => api.getTests({ 
      page, 
      limit: 20, 
      status: statusFilter || undefined 
    }),
  })

  const runTestMutation = useMutation({
    mutationFn: (testId: number) => api.runTest(testId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tests'] })
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
  })

  const deleteTestMutation = useMutation({
    mutationFn: (testId: number) => api.deleteTest(testId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tests'] })
    },
  })

  const handleRunTest = (testId: number) => {
    runTestMutation.mutate(testId)
  }

  const handleDeleteTest = (testId: number) => {
    if (confirm('Are you sure you want to delete this test?')) {
      deleteTestMutation.mutate(testId)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tests</h1>
          <p className="text-muted-foreground">
            Manage your automated browser tests
          </p>
        </div>
        <Button asChild>
          <Link to="/tests/new">
            <Plus className="h-4 w-4 mr-2" />
            New Test
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-40 h-9 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Tests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Test Suites</CardTitle>
          <CardDescription>
            Manage and execute your automated browser tests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-6 w-16" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : data?.tests.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tests.map((test: Test) => (
                  <TableRow key={test.id}>
                    <TableCell>
                      <div>
                        <Link
                          to={`/tests/${test.id}`}
                          className="font-medium hover:underline"
                        >
                          {test.name}
                        </Link>
                        <p className="text-sm text-muted-foreground truncate">
                          {test.base_url}
                        </p>
                        {test.persona_name && (
                          <p className="text-xs text-muted-foreground">
                            Persona: {test.persona_name}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(test.status)}>
                        {test.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {test.last_run_status ? (
                        <Badge variant={getStatusVariant(test.last_run_status)} className="text-xs">
                          {test.last_run_status}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatTimestamp(test.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleRunTest(test.id)}
                          disabled={runTestMutation.isPending}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Run
                        </Button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/tests/${test.id}/edit`}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteTest(test.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <TestTube className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-medium">No tests</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Get started by creating a new test.
              </p>
              <div className="mt-6">
                <Button asChild>
                  <Link to="/tests/new">
                    <Plus className="h-4 w-4 mr-2" />
                    New Test
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.pagination && data.pagination.total > data.pagination.limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} results
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page * data.pagination.limit >= data.pagination.total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
